'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** cn_scene : class that contains all data to be drawn
//***********************************************************************************
//***********************************************************************************

import { fh_polygon } from '@acenv/fh-3d-viewer';
import { cn_facing, cn_opening, cn_parking_space, cn_slab, cn_space_segment, cn_space_type, logger } from '..';
import { generate_textures_per_element } from '../utils/cn_svg_patterns';
import { CN_CURRENT_DATE, cn_transaction_manager } from '../utils/cn_transaction_manager';
import { cn_add, cn_box, cn_clone, cn_dot, cn_intersect_line, cn_mul, cn_normalize, cn_sub, cn_uuid } from '../utils/cn_utilities';
import { cn_area_trimming } from './cn_area_trimming';
import { cn_beam } from './cn_beam';
import { cn_column } from './cn_column';
import { cn_contour } from './cn_contour';
import { cn_element } from './cn_element';
import { cn_facing_trimming } from './cn_facing_trimming';
import { cn_layer } from './cn_layer';
import { cn_object_instance } from './cn_object_instance';
import { cn_pipe } from './cn_pipe';
import { cn_slab_opening } from './cn_slab_opening';
import { cn_space } from './cn_space';
import { cn_stairs } from './cn_stairs';
import { cn_vertex } from './cn_vertex';
import { cn_wall, cn_wall_shape_impact } from './cn_wall';
import { cn_scene_analyser } from './cn_scene_analyser';
import { cn_duplicator } from './cn_duplicator';
import { cn_space_dummy } from './cn_space_dummy';
import { cn_mmm_element } from './cn_mmm_element';

export class cn_scene extends cn_element {
    constructor(building) {
        super(building);
        this.class_name = 'cn_scene';
        var obj = this;
        this.removable = false;

        //*** Scene data
        this.vertices = [];
        this.walls = [];
        var outside = cn_space.outside_space(this);
        this.spaces = [outside];
        this.space_dummies = [];
        this.slab_openings = [];
        // this.bms = [];
        this.stairs = [];
        this.object_instances = [];
        this.beams = [];
        this.columns = [];
        this.pipes = [];
        this.mmm_elements = [];
        this.area_trimmings = [];
        this.facing_trimmings = [];
        this.parking_spaces = [];
        this.duplicators = [];
        this.slabs = [];

        //*** Scene volatile data
        this.building = building;
        this.storey = null;
        this.draw_samplings = false;
        this.draw_comments = true;
        this.draw_comments_application = false;
        this.draw_height_box = true;
        this.draw_layers = [];
        this.draw_space_types = [];
        this.draw_zones = [];
        this.highlighted_facing = null;
        this.highlighted_elements = new Set();
        this.texture_space_segments_per_wall = new Map();
        this._highlight_heated_spaces = false;
        this.draw_numerotation = true;
        this.draw_layer_support = '';
        this.draw_building_element_label = true;
        this.drawing_for_background = false;
        this.inactive_handler_state = null;

        //** a flag to rebuild spaces */
        this._need_rebuild_spaces = true;

        //*** Scene analyser */
        this.scene_analyser = new cn_scene_analyser(this);

        this.space_index = 0;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize_with_selection(selection) {
        return this._serialize(selection);
    }

    serialize() {
        return this._serialize(null);
    }

    _serialize(selection = null) {
        var json = this.serialize_element();

        if (selection) {
            for (var i in this.walls)
                this.walls[i].selected = false;

            for (var i in this.slab_openings)
                this.slab_openings[i].selected = false;

            // for (var i in this.bms)
            //     this.bms[i].selected = false;

            for (var i in this.stairs)
                this.stairs[i].selected = false;

            for (var i in this.object_instances)
                this.object_instances[i].selected = false;

            for (var i in this.beams)
                this.beams[i].selected = false;

            for (var i in this.columns)
                this.columns[i].selected = false;

            for (var i in this.pipes)
                this.pipes[i].selected = false;

            this.mmm_elements.forEach(e => e.selected = false);

            for (var i in selection)
                selection[i].selected = true;
        }

        //*** In the case of a selection serialization, we store also element types and objects */
        if (selection) {
            json.element_types = [];
            json.objects = [];
        }

        json.space_index = this.space_index;
        json.vertices = [];
        var n = 0;
        for (var i in this.vertices) {
            if (selection) {
                var vertex = this.vertices[i];
                var found = false;
                for (var j in vertex.walls) {
                    if (!vertex.walls[j].selected)
                        continue;
                    found = true;
                    break;
                }
                if (!found) continue;
            }
            this.vertices[i].s_index = n;
            json.vertices.push(this.vertices[i].serialize());
            n++;
        }
        json.walls = [];
        n = 0;
        var check_selection = (selection != null);
        for (var i in this.walls) {
            var wall = this.walls[i];
            if (selection && !wall.selected)
                continue;
            wall.s_index = n;
            n++;
        }
        for (var i in this.walls) {
            var wall = this.walls[i];
            if (selection && !wall.selected)
                continue;
            json.walls.push(wall.serialize_with_selection(check_selection));

            //*** Store wall and openings types */
            if (selection) {
                if (json.element_types.indexOf(wall.wall_type) < 0)
                    json.element_types.push(wall.wall_type);
                for (var j in wall.openings) {
                    if (!wall.openings[j].selected) continue;
                    var ot = wall.openings[j].opening_type
                    if (json.element_types.indexOf(ot) < 0)
                        json.element_types.push(ot);
                }
            }
        }
        json.spaces = [];

        function fully_selected(space) {
            for (var i in space.contours) {
                var ctr = space.contours[i];
                for (var j in ctr.walls) {
                    if (!ctr.walls[j].selected) return false;
                }
            }
            return true;
        }

        n = 0;
        for (var i in this.spaces) {
            if (selection && !fully_selected(this.spaces[i])) continue;
            this.spaces[i].s_index = n;
            json.spaces.push(this.spaces[i].serialize());
        }

        json.space_dummies = this.space_dummies.map(s => s.serialize());

        json.slab_openings = [];
        n = 0;
        for (var i in this.slab_openings) {
            if (selection && !this.slab_openings[i].selected) continue;
            this.slab_openings[i].s_index = n;
            n++;
            json.slab_openings.push(this.slab_openings[i].serialize());
        }

        // json.bms = [];
        // n=0;
        // for (var i in this.bms)
        // {
        //     if (selection && !this.bms[i].selected) continue;
        //     this.bms[i].s_index = n;
        //     n++;
        //     json.bms.push(this.bms[i].serialize());
        // }

        json.stairs = [];
        n = 0;
        for (var i in this.stairs) {
            if (selection && !this.stairs[i].selected) continue;
            this.stairs[i].s_index = n;
            n++;
            json.stairs.push(this.stairs[i].serialize());
        }

        json.object_instances = [];
        n = 0;
        for (var i in this.object_instances) {
            if (selection && !this.object_instances[i].selected) continue;
            this.object_instances[i].s_index = n;
            n++;
            json.object_instances.push(this.object_instances[i].serialize());
            if (selection && json.objects.indexOf(this.object_instances[i].object) < 0)
                json.objects.push(this.object_instances[i].object);
        }

        json.beams = [];
        n = 0;
        for (var i in this.beams) {
            if (selection && !this.beams[i].selected) continue;
            this.beams[i].s_index = n;
            n++;
            json.beams.push(this.beams[i].serialize());
            if (selection && json.element_types.indexOf(this.beams[i].element_type) < 0)
                json.element_types.push(this.beams[i].element_type);
        }

        json.columns = [];
        n = 0;
        for (var i in this.columns) {
            if (selection && !this.columns[i].selected) continue;
            this.columns[i].s_index = n;
            n++;
            json.columns.push(this.columns[i].serialize());
            if (selection && json.element_types.indexOf(this.columns[i].element_type) < 0)
                json.element_types.push(this.columns[i].element_type);
        }

        json.pipes = [];
        n = 0;
        for (var i in this.pipes) {
            if (selection && !this.pipes[i].selected) continue;
            this.pipes[i].s_index = n;
            n++;
            json.pipes.push(this.pipes[i].serialize());
            if (selection && json.element_types.indexOf(this.pipes[i].element_type) < 0)
                json.element_types.push(this.pipes[i].element_type);
        }

        json.mmm_elements = [];
        n = 0;
        for (var i in this.mmm_elements) {
            if (selection && !this.mmm_elements[i].selected) continue;
            this.mmm_elements[i].s_index = n;
            n++;
            json.mmm_elements.push(this.mmm_elements[i].serialize());
            if (selection && json.element_types.indexOf(this.mmm_elements[i].composition) < 0)
                json.element_types.push(this.mmm_elements[i].composition);
        }

        json.area_trimmings = [];
        n = 0;
        for (var i in this.area_trimmings) {
            if (selection && !this.area_trimmings[i].selected) continue;
            this.area_trimmings[i].s_index = n;
            n++;
            json.area_trimmings.push(this.area_trimmings[i].serialize());
        }

        json.facing_trimmings = [];
        for (var i in this.facing_trimmings) {
            if (selection && !this.facing_trimmings[i].selected) continue;
            json.facing_trimmings.push(this.facing_trimmings[i].serialize());
        }

        json.parking_spaces = [];
        for (var i in this.parking_spaces) {
            if (selection && !this.parking_spaces[i].selected) continue;
            json.parking_spaces.push(this.parking_spaces[i].serialize());
        }

        json.duplicators = this.duplicators.map(dup => dup.serialize());

        return json;
    }

    static unserialize(json, building) {
        if (typeof (json.vertices) != 'object')
            throw 'Error reading scene : \'vertices\' not found';
        if (typeof (json.walls) != 'object')
            throw 'Error reading scene : \'walls\' not found';
        if (typeof (json.spaces) != 'object')
            throw 'Error reading scene : \'spaces\' not found';

        var scene = new cn_scene(building);
        scene.unserialize_element(json);
        for (var i in json.vertices)
            cn_vertex.unserialize(json.vertices[i], scene);

        for (var i in json.walls)
            cn_wall.unserialize(json.walls[i], scene);

        if (typeof (json.space_index) == 'number')
            scene.space_index = json.space_index;
        scene.spaces = [];
        json.spaces.forEach(sp => cn_space.unserialize(sp, scene));
        json.spaces.forEach(sp => cn_space.unserialize_parent_space(sp, scene));

        if (scene.spaces.length == 0) {
            var outside = cn_space.outside_space(scene);
            scene.spaces = [outside];
        }

        scene.space_dummies = [];
        if (json.space_dummies)
            json.space_dummies.forEach(js => cn_space_dummy.unserialize(js, scene));

        scene.slab_openings = [];
        for (var i in json.slab_openings)
            cn_slab_opening.unserialize(json.slab_openings[i], scene);

        // scene.bms = [];
        // for (var i in json.bms)
        //     cn_background_map.unserialize(json.bms[i]);

        scene.stairs = [];
        for (var i in json.stairs)
            cn_stairs.unserialize(json.stairs[i], scene);

        scene.object_instances = [];
        for (var i in json.object_instances)
            cn_object_instance.unserialize(json.object_instances[i], scene);

        scene.beams = [];
        for (var i in json.beams)
            cn_beam.unserialize(json.beams[i], scene);

        scene.columns = [];
        for (var i in json.columns)
            cn_column.unserialize(json.columns[i], scene);

        scene.pipes = [];
        for (var i in json.pipes)
            cn_pipe.unserialize(json.pipes[i], scene);

        scene.mmm_elements = [];
        for (var i in json.mmm_elements)
            cn_mmm_element.unserialize(json.mmm_elements[i], scene);

        scene.area_trimmings = [];
        for (var i in json.area_trimmings)
            cn_area_trimming.unserialize(json.area_trimmings[i], scene);

        scene.facing_trimmings = [];
        for (var i in json.facing_trimmings)
            cn_facing_trimming.unserialize(json.facing_trimmings[i], scene);

        scene.parking_spaces = [];
        for (var i in json.parking_spaces)
            cn_parking_space.unserialize(json.parking_spaces[i], scene);

        if (json.duplicators)
            json.duplicators.forEach(jsondup => cn_duplicator.unserialize(jsondup, scene));

        return scene;
    }

    /**
     * Checks unicity of IDS inside a tree of elements.
     * @param {string[]} id_list
     */
    check_id(id_list) {
        super.check_id(id_list);
        this.iterate_elements(e => e.check_id(id_list));
    }

    //***********************************************************************************
    /**
     * Checks existence of an element in the scene
     * @param {any} element
     * @returns {boolean}
     */
    check_element(element) {
        if (element == null) return false;
        if (element.constructor == cn_vertex)
            return this.vertices.indexOf(element) >= 0;
        if (element.constructor == cn_wall)
            return this.walls.indexOf(element) >= 0;
        if (element.constructor == cn_space)
            return this.spaces.indexOf(element) >= 0;
        if (element.constructor == cn_slab_opening)
            return this.slab_openings.indexOf(element) >= 0;
        if (element.constructor == cn_stairs)
            return this.stairs.indexOf(element) >= 0;
        if (element.constructor == cn_object_instance)
            return this.object_instances.indexOf(element) >= 0;
        if (element.constructor == cn_beam)
            return this.beams.indexOf(element) >= 0;
        if (element.constructor == cn_column)
            return this.columns.indexOf(element) >= 0;
        if (element.constructor == cn_pipe)
            return this.pipes.indexOf(element) >= 0;
        if (element.constructor == cn_mmm_element)
            return this.mmm_elements.indexOf(element) >= 0;
        if (element.constructor == cn_area_trimming)
            return this.area_trimmings.indexOf(element) >= 0;
        if (element.constructor == cn_facing_trimming)
            return this.facing_trimmings.indexOf(element) >= 0;
        if (element.constructor == cn_parking_space)
            return this.parking_spaces.indexOf(element) >= 0;
        if (element.constructor == cn_space_segment)
            return this.spaces.some(space => space.space_segments.some(ssl => ssl.includes(element)));
        if (element.constructor == cn_slab) {
            return (this.spaces.includes(element.spaces[0]) || this.spaces.includes(element.spaces[1]));
        }
        return false;
    }

    //***********************************************************************************
    //**** run scene diagnostic
    //***********************************************************************************
    run_diagnostic() {
        //** a flag to rebuild spaces */
        this.scene_analyser.process();
        //if (this._need_rebuild_spaces)
        //    this.build_automatic_spaces();

        this.update_zones();
        this.update_deep()
    }

    //***********************************************************************************
    //**** unmerge 2 similar vertices.
    //***********************************************************************************
    unmerge_vertices(v0, v1) {
        if (this.vertices.indexOf(v1) >= 0) return;
        this.vertices.push(v1);

        for (var i in v0.walls) {
            var wall = v0.walls[i];
            if (v1.walls.indexOf(wall) < 0) continue;
            if (wall.vertices[0] == v0)
                wall.vertices[0] = v1;
            else if (wall.vertices[1] == v0)
                wall.vertices[1] = v1;
        }
    }

    clear_highlighted_facing() {
        this.highlighted_facing = null;
    }

    /**
     *
     * @param {cn_facing} facing
     */
    highlight_facing(facing) {
        this.highlighted_facing = facing;
    }

    _define_highlighted_elements() {
        this.highlighted_elements.clear();
        this.texture_space_segments_per_wall.clear();
        if (this.highlighted_facing) {
            [
                ...this.beams,
                ...this.columns,
                ...this.pipes,
                ...this.mmm_elements,
                ...this.stairs,
                ...this.object_instances
            ].filter(elt => !!elt.facing).forEach(elt => {
                if (elt.facing.ID === this.highlighted_facing.ID) {
                    this.highlighted_elements.add(elt.ID);
                }
            })
            this.walls.forEach(wall => {
                const hss = []
                wall.space_segments.forEach(ss => {
                    if (ss.facing && ss.facing.ID === this.highlighted_facing.ID) {
                        hss.push({ ID: ss.ID, style: `style="fill:#D4AF37"` })
                    }
                })
                this.texture_space_segments_per_wall.set(wall.ID, hss);
                wall.openings.forEach(opening => {
                    if (opening.valid && opening.facings.some(fl => fl.some(f => f && f.ID === this.highlighted_facing.ID))) {
                        this.highlighted_elements.add(opening.ID);
                    }
                })
            });
            this.spaces.filter(space => space.facings.some(f => f && f.ID === this.highlighted_facing.ID)).forEach(space => {
                this.highlighted_elements.add(space.ID);
            });
            this.facing_trimmings.filter(ft => ft.facing && ft.facing.ID === this.highlighted_facing.ID).forEach(ft => {
                this.highlighted_elements.add(ft.ID);
            })
        }
    }

    //***********************************************************************************
    //**** Refresh
    //***********************************************************************************
    draw(camera) {
        let html = '';
        this._define_highlighted_elements();
        const space_highlighted_elements = new Map();
        const mandatory_slabs_for_colored_elements = []
        const splitted_sections = this._extract_splitted_polygons_from_layers_sections();
        const textures_per_element = this._collect_colorised_elements(splitted_sections);
        const textures_generation = generate_textures_per_element(textures_per_element);
        const textures_id_per_element = textures_generation.textures;
        textures_id_per_element.forEach((value, id) => {
            textures_id_per_element.set(id, `style="${value}"`);
        });
        this.highlighted_elements.forEach(el => {
            textures_id_per_element.set(el, `style="fill:#D4AF37"`)
        })
        html += textures_generation.html;

        if (this.draw_comments_application && this.storey && this.storey.markers) {
            this.storey.markers.forEach(marker => {
                mandatory_slabs_for_colored_elements.push(...this.storey.slabs.filter(slab => marker.element && marker.element.ID === slab.ID));
            })
        }
        if (this.draw_layers.length) {
            this.draw_layers.forEach(layer => {
                mandatory_slabs_for_colored_elements.push(...this.storey.slabs.filter(slab => layer.elements.find(el => el.obj === slab.ID && el.storey === this.storey.ID) || layer.elements_types.includes(slab.slab_type.ID)));
            });
        }

        if (this.draw_zones.length) {
            const drawables_zones = [];
            for (const zoning_type in this.building.zones) {
                drawables_zones.push(...this.building.get_zones(zoning_type).filter(zone => this.draw_zones.includes(zone.ID)));
            }
            drawables_zones.forEach((zone, i) => {
                zone.rooms.filter(room => room.storey === this.storey.ID).forEach(room => {
                    space_highlighted_elements.set(room.space, `style="fill:${zone.color};opacity:1"`);
                });
            });
        } else if (this.draw_space_types.length) {
            const space_type_color_map = new Map();
            this.draw_space_types.forEach(spt => space_type_color_map.set(spt.ID, spt.color));
            this.spaces.filter(space => space.space_type && space_type_color_map.has(space.space_type.ID)).forEach(space => {
                space_highlighted_elements.set(space.ID, `style="fill:${space_type_color_map.get(space.space_type.ID)};opacity:1"`)
            })
        }

        if (this._highlight_heated_spaces) {
            this.spaces.forEach(space => {
                space_highlighted_elements.set(space.ID, `style="fill:${space.is_heated() ? '#E49B9D' : '#9BC1E4'};opacity:1"`)
            });
        }

        //*** draw slab openings from storey bellow */
        const storey_bellow = this.storey.get_previous_storey();
        if (storey_bellow) {
            storey_bellow.scene.stairs.forEach(stairs => html += stairs.draw_slab_opening(camera));
            storey_bellow.scene.spaces.forEach(space => html += space.draw_slab_opening(camera));
        }

        camera._svg_context.textures_per_element = textures_per_element;
        html += this._draw_scene(camera, this.draw_numerotation, true, space_highlighted_elements, mandatory_slabs_for_colored_elements, textures_id_per_element, splitted_sections);
        camera._svg_context.textures_per_element = new Map();
        return html;
    }

    draw_as_backround(camera) {
        return this._draw_scene(camera);
    }

    _draw_scene(camera, draw_numerotation = false, draw_vertices = false, space_highlighted_elements = new Map(), mandatory_slabs_for_colored_elements = [], textures_id_per_element = new Map(), splitted_sections = []) {
        let html = ''
        const space_added_classes = this.draw_space_types.length ? ['no_children', 'draw_standalone'] : [];
        this.spaces.forEach(space => {
            html += space.draw(camera, space_added_classes, space_highlighted_elements.get(space.ID) || textures_id_per_element.get(space.ID) || '', draw_numerotation);
        });

        const openings = this.walls.reduce((agg, wall) => agg.concat(wall.openings), []);
        const niches = this.walls.reduce((agg, wall) => agg.concat(wall.niches), []);

        if (!this.slabs) this.slabs = [];

        let svg_context = camera.get_svg_context();

        this.inactive_handler_state = null;
        [
            ...mandatory_slabs_for_colored_elements,
            ...this.slabs,
            ...this.parking_spaces,
            ...this.mmm_elements,
            ...this.columns,
            ...this.slab_openings,
            ...this.stairs,
            ...this.object_instances,
            ...this.beams,
        ].forEach(element => {
            if (!svg_context.draw_filter(element)) return;
            html += this._handle_inactive_container(camera, element);
            html += element.draw(camera, [], textures_id_per_element.get(element.ID) || '');
        });

        this.pipes.forEach(pipe => {
            if (!svg_context.draw_filter(pipe)) return;
            let pipe_style = textures_id_per_element.get(pipe.ID) || '';
            let section_style = pipe_style;
            if (this.draw_layers && this.draw_layers.length && !pipe_style) {
                pipe_style = 'style="fill:url(#pattern-wave);"';
                section_style = 'style="fill:rgb(150,150,150);opacity:1;"';
            }
            html += this._handle_inactive_container(camera, pipe);
            html += pipe.draw(camera, [], pipe_style, section_style);
        });

        this.mmm_elements.forEach(mmm_element => {
            if (!svg_context.draw_filter(mmm_element)) return;
            let mmm_element_style = textures_id_per_element.get(mmm_element.ID) || '';
            html += this._handle_inactive_container(camera, mmm_element);
            html += mmm_element.draw(camera, [], mmm_element_style);
        });

        this.walls.forEach(wall => {
            if (!svg_context.draw_filter(wall)) return;
            let wall_style = textures_id_per_element.get(wall.ID) || '';
            if (this.draw_layers && this.draw_layers.length && !wall_style) {
                wall_style = 'style="opacity:1"';
            }
            html += this._handle_inactive_container(camera, wall);
            html += wall.draw(camera, [], wall_style, this.texture_space_segments_per_wall.get(wall.ID) || []);

            wall.wall_interfaces.forEach(wi => {
                let wi_style = textures_id_per_element.get(wi.ID) || '';
                if (wi_style == '') return;
                html += this._handle_inactive_container(camera, wall);
                html += wi.draw(camera, [], wi_style, this.texture_space_segments_per_wall.get(wi.ID) || []);
            });

            wall.space_segments.forEach(ss => {
                let wi_style = textures_id_per_element.get(ss.ID) || '';
                if (wi_style == '') return;
                html += this._handle_inactive_container(camera, wall);
                html += ss.draw_substrate(camera, [], wi_style);
            });
        });

        if (draw_vertices) {
            this.vertices.forEach(element => {
                if (!svg_context.draw_filter(element)) return;
                html += this._handle_inactive_container(camera, element);
                html += element.draw(camera, [], textures_id_per_element.get(element.ID) || '');
            });
        }

        openings.forEach(opening => {
            if (!svg_context.draw_filter(opening)) return;
            html += this._handle_inactive_container(camera, opening);
            html += opening.draw(camera, [], textures_id_per_element.get(opening.ID) || '');
        });

        niches.forEach(niche => {
            if (!svg_context.draw_filter(niche)) return;
            html += this._handle_inactive_container(camera, niche);
            html += niche.draw(camera, [], textures_id_per_element.get(niche.ID) || '');
        });

        if (splitted_sections.length) {
            html += this._draw_layer_sections(splitted_sections, camera, textures_id_per_element);
        }

        if (this.draw_samplings) {
            this.storey.samplings.filter(sampling => !sampling.roof && (!this.draw_layer_support || this.draw_layer_support === sampling.support))
                .forEach(sampling => {
                    if (!svg_context.draw_filter(sampling)) return;
                    sampling.update();
                    html += this._handle_inactive_container(camera, sampling);
                    html += sampling.draw(camera);
                });
        }

        if (this.draw_comments && this.storey && this.storey.markers) {
            this.storey.markers.filter(marker => !marker.roof).forEach(marker => {
                if (!svg_context.draw_filter(marker)) return;
                marker.update();
                let pic = !!marker['pictures'] && !!marker['pictures'].length;
                html += this._handle_inactive_container(camera, marker);
                html += marker.draw(camera, [], false, pic);
            });
        }

        if (this.draw_building_element_label) {
            [
                ...this.mmm_elements,
                ...this.beams,
                ...this.columns,
                ...this.pipes,
                ...openings,
                ...this.object_instances,
            ].filter(el => svg_context.draw_filter(el)).forEach(el => html += el.draw_id_label(camera, this.storey))
        }

        this.facing_trimmings.filter(ft => this.highlighted_elements.has(ft.ID)).forEach(ft => {
            if (!svg_context.draw_filter(ft)) return;
            html += ft.draw(camera, [], textures_id_per_element.get(ft.ID) || '')
        })

        html += '</g>';

        return html;
    }

    _handle_inactive_container(camera, element) {
        let html = ''
        const inactive = (camera.element_filter && !camera.element_filter(element));
        if (inactive !== this.inactive_handler_state) {
            if (this.inactive_handler_state !== null) {
                html += '</g>';
            }
            html += `<g ${inactive ? 'opacity="0.3"' : ''}>`
            this.inactive_handler_state = inactive;
        }
        return html;
    }

    _draw_layer_sections(splitted_sections, camera, textures_id_per_element) {
        let html = ''
        if (this.draw_layers) {
            splitted_sections.forEach(section => {
                const shape = section.contour;
                shape.forEach((contour, i) => {
                    if (i === 0) {
                        html += `<path  d="`;
                    }
                    contour.inner_contour.forEach((contour, i) => {
                        if (i === 0) {
                            html += 'M ';
                        } else if (i === 1) {
                            html += 'L ';
                        }
                        const p = camera.world_to_screen(contour);
                        html += `${p[0]} ${p[1]} `;
                    });
                    if (i === shape.length - 1) {
                        html += 'Z "';
                        html += ` ${textures_id_per_element.get(shape[0].ID) || ''} />`;
                    }
                });
            });
        }
        return html;
    }

    /**
     * Split sections in intersects and separates polygons
     *
     * @returns Array
     */
    _extract_splitted_polygons_from_layers_sections() {
        const splitted_polygons = [];
        if (this.draw_layers.length) {
            const sections_polygons = this.draw_layers.reduce((agg, layer) => agg.concat(layer.sections.filter(section => section.storey === this.storey.ID
                && (!this.draw_layer_support || this.draw_layer_support === section.support)).concat(layer.lines.filter(line => line.storey === this.storey.ID
                    && (!this.draw_layer_support || this.draw_layer_support === line.support))).map(section => {
                        const polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);
                        polygon.add_contour(section.shape.build_3d_contour(0, true));
                        polygon.compute_contours();
                        return { polygon, layer };
                    })), []);
            sections_polygons.forEach((section, index) => {
                const p = { polygon: section.polygon.clone(), layers: [section.layer] };
                const other_polygons = sections_polygons.slice(index + 1);
                splitted_polygons.forEach(pol => {
                    p.polygon.substracts(pol.polygon);
                    other_polygons.forEach(o => {
                        const op = o.polygon.clone()
                        op.substracts(pol.polygon)
                        return { polygon: op, layer: o.layer }
                    });
                });
                this._calculate_polygons_intersections(p, other_polygons, splitted_polygons, false);
            });
            sections_polygons.forEach((section, index) => {
                const l = section.polygon.clone();
                sections_polygons.forEach((s, i) => {
                    if (index !== i) {
                        l.substracts(s.polygon);
                    }
                });
                if (l.contour_sizes.length > 0) {
                    l.split().forEach(pol => {
                        splitted_polygons.push({ polygon: pol, layers: [section.layer] });
                    });
                }
            });
        }
        return splitted_polygons.map(pol => {
            return { contour: cn_contour.build_from_polygon(pol.polygon), layers: pol.layers }
        });
    }

    /**
     * Reference all colors to applicate per element
     *
     * @returns {Map}
     */
    _collect_colorised_elements(splitted_sections) {
        const colorsPerElement = new Map();
        if (this.draw_layers.length) {
            this.draw_layers.forEach(layer => {
                layer.elements.filter(el => el.storey === this.storey.ID && (!this.draw_layer_support || this.draw_layer_support === el.support)).forEach(element => {
                    colorsPerElement.set(element.obj, [...(colorsPerElement.get(element.obj) || []),
                    { color: layer.color, opacity: layer.opacity, texture: layer.stripes,dasharray: layer.dasharray }]);
                });
                const impacted_elements_types = [...layer.elements_types];
                if (impacted_elements_types) {
                    const elements_by_type = this._collect_elements_by_type(impacted_elements_types);
                    impacted_elements_types.reduce((a, b) => a.concat(elements_by_type.get(b)), []).forEach(element => {
                        colorsPerElement.set(element, [...(colorsPerElement.get(element) || []),
                        { color: layer.color, opacity: layer.opacity, texture: layer.stripes, dasharray: layer.dasharray }]);
                    });
                }
            });
            splitted_sections.forEach(section => {
                colorsPerElement.set(section.contour[0].ID, section.layers.map(layer => {
                    return { color: layer.color, opacity: layer.opacity, texture: layer.stripes, dasharray: layer.dasharray }
                }));
            });
        }

        if (this.draw_comments_application && this.storey && this.storey.markers) {
            this.storey.markers.forEach(marker => {
                if (!marker.type && !marker.shape && marker.color && marker.element) {
                    colorsPerElement.set(marker.element.ID, [...(colorsPerElement.get(marker.element.ID) || []), { color: marker.color, opacity: 1, texture: 'full', dasharray: "" }]);
                }
            });
        }

        return colorsPerElement;
    }

    /**
     * Collect element by scope of elements types
     *
     * @param {Array<string>} scope
     */
    _collect_elements_by_type(scope) {
        const result = new Map();
        if (!this.draw_layer_support || this.draw_layer_support === 'wall') {
            this.walls.filter(wall => wall.wall_interfaces.length < 2).forEach(wall => {
                [wall.wall_type, ...wall.upper_wall_types].filter(wt => scope.includes(wt.ID)).forEach(wt => {
                    result.set(wt.ID, [...(result.get(wt.ID) || []), wall.ID]);
                });
            });
            this.walls.filter(wall => wall.wall_interfaces.length >= 2).forEach(wall => {
                wall.wall_interfaces.forEach(wi => {
                    wi.get_wall_types().filter(wt => scope.includes(wt.ID)).forEach(wt => {
                        result.set(wt.ID, [...(result.get(wt.ID) || []), wi.ID]);
                    })
                });
            });

            this.walls.forEach(wall => {
                wall.space_segments.forEach(ss => {
                    ss.in_scope(scope).forEach(id => result.set(id, [...(result.get(id) || []), ss.ID]));
                });
            });
        }
        if (!this.draw_layer_support || this.draw_layer_support === 'work' || this.draw_layer_support === 'other') {
            this.walls.reduce((a, b) => a.concat(b.openings), []).filter(opening => scope.includes(opening.opening_type.ID)).forEach(opening => result.set(opening.opening_type.ID, [...(result.get(opening.opening_type.ID) || []), opening.ID]));
            this.object_instances.filter(obj => scope.includes(obj.object.ID)).forEach(obj => result.set(obj.object.ID, [...(result.get(obj.object.ID) || []), obj.ID]));
            this.beams.filter(beam => scope.includes(beam.element_type.ID)).forEach(beam => result.set(beam.element_type.ID, [...(result.get(beam.element_type.ID) || []), beam.ID]));
            this.columns.filter(column => scope.includes(column.element_type.ID)).forEach(column => result.set(column.element_type.ID, [...(result.get(column.element_type.ID) || []), column.ID]));
            this.pipes.filter(pipe => scope.includes(pipe.element_type.ID)).forEach(pipe => result.set(pipe.element_type.ID, [...(result.get(pipe.element_type.ID) || []), pipe.ID]));
            this.mmm_elements.filter(mmm => scope.includes(mmm.composition.ID)).forEach(mmm => result.set(mmm.composition.ID, [...(result.get(mmm.composition.ID) || []), mmm.ID]));
        }
        if (!this.draw_layer_support || this.draw_layer_support === 'floor') {
            this.storey.slabs.filter(slab => scope.includes(slab.slab_type.ID)).forEach(slab => result.set(slab.slab_type.ID, [...(result.get(slab.slab_type.ID) || []), slab.ID]));
            this.storey.scene.slab_openings.filter(so => so.slab_type && scope.includes(so.slab_type.ID)).forEach(so => result.set(so.slab_type.ID, [...(result.get(so.slab_type.ID) || []), so.ID]));
        }

        return result;
    }

    _calculate_polygons_intersections(p, others_polygons, result, intersection) {
        if (others_polygons.length > 0) {
            const next = others_polygons[0];
            const intersect = p.polygon.clone();
            intersect.intersects(next.polygon);
            if (intersect && intersect.contour_sizes.length > 0) {
                p.polygon.substracts(intersect);
                const new_p = { polygon: intersect, layers: [...p.layers, next.layer] };
                this._calculate_polygons_intersections(new_p, others_polygons.slice(1), result, true);
            }
            if (p.polygon.contour_sizes.length > 0) {
                this._calculate_polygons_intersections(p, others_polygons.slice(1), result, intersection);
            }
        } else if (intersection) {
            result.push(p);
        }
        return result
    }

    simple_draw(camera, add_classes = ['']) {
        let svg_context = camera.get_svg_context();
        let html = '';
        if (this.spaces.length > 1) {
            this.spaces.forEach(space => {
                if (!svg_context.draw_filter(space)) return;
                html += space.draw(camera, add_classes, '', false, true);
            });
        } else {
            [...this.mmm_elements, ...this.columns, ...this.pipes, ...this.beams,].filter(el => svg_context.draw_filter(el)).forEach(el => html += el.draw(camera, [...add_classes, 'simple_draw']));
        }
        return html;
    }

    //***********************************************************************************
    //**** Set status
    //***********************************************************************************
    set_status(status = 0) {
        for (var i in this.spaces)
            this.spaces[i].status = status;
        for (var i in this.walls) {
            this.walls[i].status = status;
            for (var j in this.walls[i].openings)
                this.walls[i].openings[j].status = status;
        }
        for (var i in this.vertices)
            this.vertices[i].status = status;
        for (var i in this.slab_openings)
            this.slab_openings[i].status = status;
        for (var i in this.stairs)
            this.stairs[i].status = status;
        for (var i in this.object_instances)
            this.object_instances[i].status = status;
        for (var i in this.beams)
            this.beams[i].status = status;
        for (var i in this.columns)
            this.columns[i].status = status;
        for (var i in this.pipes)
            this.pipes[i].status = status;
        for (var i in this.mmm_elements)
            this.mmm_elements[i].status = status;
    }

    //***********************************************************************************
    //**** Get a space by its ID
    //***********************************************************************************
    get_space(id) {
        for (var i in this.spaces) {
            if (this.spaces[i].ID == id) return this.spaces[i];
        }
        return null;
    }

    get_opening(id) {
        for (var i in this.walls) {
            let wall = this.walls[i];
            for (var j in wall.openings) {
                if (wall.openings[j].ID == id) return wall.openings[j];
            }
        }
        return null;

    }

    //***********************************************************************************
    //**** find a vertex
    //***********************************************************************************
    find_vertex(pt, tolerance = 0.01) {
        for (var i in this.vertices) {
            if (this.vertices[i].contains(pt, tolerance))
                return this.vertices[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a wall by a point
    //***********************************************************************************
    find_wall(pt, tolerance = 0, avoided_walls = []) {
        let best_distance = 0;
        let best_wall = null;
        this.walls.filter(w => !!w && !avoided_walls.includes(w)).forEach(w => {
            const l = w.contains(pt, tolerance);
            if (l === false) return;
            if (!best_wall || l > best_distance) {
                best_wall = w;
                best_distance = l;
            }
        });
        return best_wall;
    }

    //***********************************************************************************
    /**
     * find a space by a point
     * @param {number[]} pt
     * @param {number} tolerance
     * @returns {import('./cn_space_segment').cn_space_segment}
     */
    find_space_segment(pt, tolerance) {
        let wall = this.find_wall(pt, tolerance);
        if (!wall) return null;
        let wall_side = wall.find_support_wall_side(pt);
        let abcissa = wall.compute_abcissa(pt);
        return wall.find_space_segment(wall_side, abcissa);
    }

    //***********************************************************************************
    /**
     * find an opening by a point
     * @param {number[]} pt
     * @param {number} tolerance
     * @returns {cn_opening}
     */
    find_opening(pt, tolerance = 0) {
        let opening = null;
        this.walls.some(wall => {
            const y = cn_dot(wall.bounds.normal, cn_sub(pt, wall.bounds.pmin));
            if (y < -tolerance) return false;
            if (y > wall.wall_type.thickness + tolerance) return false;
            opening = wall.find_opening(pt);
            if (opening) return true;
        });
        return opening;
    }

    //***********************************************************************************
    /**
     * find an opening by a point
     * @param {number[]} pt
     * @param {number} tolerance
     * @returns {cn_opening}
     */
    find_niche(pt, tolerance = 0) {
        let niche = null;
        this.walls.some(wall => {
            niche = wall.niches.find(n => n.contains(pt, tolerance));
            if (niche) return true;
        });
        return niche;
    }

    //***********************************************************************************
    /**
     * find a space by a point
     * @param {number[]} pt
     * @param {boolean} inner
     * @returns {cn_space}
     */
    find_space(pt, inner = false) {
        if (this.spaces.length == 1)
            return this.spaces[0];
        for (var i = 0; i < this.spaces.length; i++) {
            if (this.spaces[i].contains(pt, inner))
                return this.spaces[i];
        }
        return null;
    }

    //***********************************************************************************
    /**
     * find a space by a point
     * @param {number[]} pt
     * @returns {cn_space}
     */
    find_space_dummy(pt) {
        return this.space_dummies.find(s => s.contains(pt, 0)) || null;
    }

    // find_bm(pt, inner=false) {
    //     logger.log("find bms");
    //     logger.log(this.bms);
    // 	// if (this.bms.length == 1)
    // 	// 	return this.bms[0];
    // 	for (var i=0;i<this.bms.length;i++)
    // 	{
    // 		if (this.bms[i].contains(pt, inner))
    // 			return this.bms[i];
    // 	}
    // 	return null;
    // }

    //***********************************************************************************
    //**** find a slab by a point
    //***********************************************************************************
    find_slab(pt) {
        for (var i = 0; i < this.slabs.length; i++) {
            if (this.slabs[i].spaces[0] == null && this.slabs[i].spaces[1] == null) continue;
            if (this.slabs[i].contains(pt))
                return this.slabs[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a slab opening by a point
    //***********************************************************************************
    find_slab_opening(pt) {
        for (var i = 0; i < this.slab_openings.length; i++) {
            if (this.slab_openings[i].contains(pt))
                return this.slab_openings[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a slab by a point
    //***********************************************************************************
    find_stairs(pt) {
        for (var i = 0; i < this.stairs.length; i++) {
            if (this.stairs[i].contains(pt))
                return this.stairs[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find an object instance by a point
    //***********************************************************************************
    find_object_instance(pt, tolerance = 0) {
        for (var i = 0; i < this.object_instances.length; i++) {
            if (this.object_instances[i].contains(pt, tolerance))
                return this.object_instances[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a beam by a point
    //***********************************************************************************
    find_beam(pt, tolerance = 0) {
        for (var i = 0; i < this.beams.length; i++) {
            if (this.beams[i].contains(pt, tolerance))
                return this.beams[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a columns by a point
    //***********************************************************************************
    find_column(pt, tolerance = 0) {
        for (var i = 0; i < this.columns.length; i++) {
            if (this.columns[i].contains(pt, tolerance))
                return this.columns[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a pipe by a point
    //***********************************************************************************
    find_pipe(pt, tolerance = 0) {
        for (var i = 0; i < this.pipes.length; i++) {
            if (this.pipes[i].contains(pt, tolerance))
                return this.pipes[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a mmm element by a point
    //***********************************************************************************
    find_mmm_element(pt, tolerance = 0) {
        for (var i = 0; i < this.mmm_elements.length; i++) {
            if (this.mmm_elements[i].contains(pt, tolerance))
                return this.mmm_elements[i];
        }
        return null;
    }
    //***********************************************************************************
    //**** find a area trimming by a point
    //***********************************************************************************
    find_area_trimming(pt) {
        for (var i = 0; i < this.area_trimmings.length; i++) {
            if (this.area_trimmings[i].contains(pt))
                return this.area_trimmings[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a facing trimming by a point
    //***********************************************************************************
    find_facing_trimming(pt) {
        for (var i = 0; i < this.facing_trimmings.length; i++) {
            if (this.facing_trimmings[i].contains(pt))
                return this.facing_trimmings[i];
        }
        return null;
    }

    //***********************************************************************************
    //**** find a parking space
    //***********************************************************************************
    find_parking_space(pt) {
        return this.parking_spaces.find(ps => ps.contains(pt)) || null;
    }

    /**
     * returns all the openings of the scene
     * @param {boolean} only_valid
     * @returns
     */
    get_openings(only_valid = true) {
        var openings = [];
        this.walls.forEach(wall => {
            if (!only_valid)
                openings = openings.concat(wall.openings);
            else
                wall.openings.forEach(op => {
                    if (op.valid) openings.push(op);
                });
        });
        return openings;
    }

    //***********************************************************************************
    //**** update spaces
    //***********************************************************************************
    update_spaces() {
        this.slab_openings.forEach(so => so.update());
        for (var i in this.spaces)
            this.spaces[i].update();
    }

    update_space_numerotation() {
        for (var i in this.spaces)
            this.spaces[i].update_numerotation();
    }

    /**
     * update zones
     */
    update_zones() {
        if (this.storey) {
            const spaces_id = this.spaces.map(space => space.ID);
            Object.entries(this.building.zones).forEach(([zoning_type, zones]) => {
                zones.forEach(zone => {
                    zone.rooms.filter(room => room.storey === this.storey.ID && !spaces_id.includes(room.space)).forEach(room => {
                        zone.remove_room(room.space, room.storey);
                    });
                });
            });
        }
    }

    //***********************************************************************************
    //**** update
    //***********************************************************************************
    update() {
        this.update_spaces();
        this.update_space_numerotation();
        this.update_zones();
    }

    update_deep() {
        this.scene_analyser.process(this.parent.transaction_manager);

        //*** Exterior space */
        if (this.storey && this.storey.exterior) {
            this.storey.ignored_polygon = null;
            var floor_storey = this.building.storeys[this.building.storey_0_index];
            if (floor_storey) {
                var exterior_space = floor_storey.scene.spaces.find(sp => sp.outside);
                if (exterior_space)
                    this.storey.ignored_polygon = exterior_space.get_inner_polygon().clone();
            }
        }

        this.facing_trimmings.forEach(at => {
            at.update();
        });
        for (var i in this.spaces) {
            this.spaces[i].update_deep();
            this.spaces[i].has_roof = (!this.spaces[i].outside);
            this.spaces[i].indoor = (!this.spaces[i].outside);
        }

        if (this.storey && this.storey.exterior) {
            this.spaces.forEach(sp => {
                sp.indoor = false;
                sp.has_roof = false;
            });
        } else {
            const balconies = this.walls.filter(w => w.balcony);
            const free_wall_interfaces = this.walls.map(w => w.wall_interfaces.filter(wi => wi.wall.wall_type.free)).flat();
            while (true) {
                var change = false;
                //*** Has Roof characteristics is transmitted by balconies*/
                balconies.forEach(w => {
                    w.wall_interfaces.filter(wi => wi.spaces[0].has_roof != wi.spaces[1].has_roof).forEach(wi => {
                        wi.spaces[0].has_roof = false;
                        wi.spaces[1].has_roof = false;
                        wi.spaces[0].indoor = false;
                        wi.spaces[1].indoor = false;
                        change = true;
                    });
                })
                //*** Indoor characteristics is transmitted by fre wall interfaces*/
                free_wall_interfaces.filter(wi => wi.spaces[0].indoor != wi.spaces[1].indoor).forEach(wi => {
                    wi.spaces[0].indoor = false;
                    wi.spaces[1].indoor = false;
                    change = true;
                });
                if (!change) break;
            }
        }
        for (var i in this.stairs) {
            this.stairs[i].update_deep();
        }
        this.area_trimmings.forEach(at => {
            at.update();
        });

        this.object_instances.forEach(oi => oi.update_deep());

        this.building.compute_area_contexts();
        this.building.update_roofs();

        if (this.storey)
            return this.storey.update_markers();
    }

    /**
     * Makes all necessary updates for the scene
     * @param {boolean} force_rebuild if true, forces the spaces to be rebuilt.
     */
    full_update(force_rebuild = false) {
        if (force_rebuild) this._need_rebuild_spaces = true;
        this.update();
        this.update_deep();
        if (this.storey) {
            const roof_footprint = this.storey.build_slab_polygon(0, false, false);
            const upper_level = this.building.find_storey_by_index(this.storey.storey_index + 1);
            let upper_footprint = null;
            if (upper_level) {
                upper_footprint = upper_level.build_slab_polygon(0, true, false);
                if (upper_footprint.get_area() < 0.1) upper_footprint = null;
                if (upper_footprint) roof_footprint.substracts(upper_footprint);
            }
            this.storey.update_roof(roof_footprint, upper_footprint);
            this.storey.update_slabs();
        }
        if (this.building && this.building.transaction_manager) {
            this.building.transaction_manager.call('rebuild_roof');
        }
    }

    //***********************************************************************************
    //**** Check walls and vertices
    //***********************************************************************************
    check_vertices(vertices = [], display_log = false) {

        if (vertices.length == 0) vertices = this.vertices;

        //*** Check vertices ambiguities : 2 walls starting from each vertex must not form an angle < 1°
        var threshold = Math.PI * 1 / 180;
        for (var i in this.vertices) {
            var vertex = this.vertices[i];
            for (var j = 0; j < vertex.angles.length; j++) {
                var a0 = vertex.angles[j];
                var a1 = (j < vertex.angles.length - 1) ? vertex.angles[j + 1] : vertex.angles[0] + Math.PI * 2;
                if (Math.abs(a1 - a0) < threshold) {
                    if (display_log) logger.log(`cn_scene.check_vertices: close wall angles [${a0}] and [${a1}] on vertex`, vertex);
                    return false;
                }
            }
        }
        return true;
    }

    check_walls(walls = [], avoided_walls = [], display_log = false) {
        return;
    }

    //***********************************************************************************
    //**** Insert one wall
    //***********************************************************************************
    insert_wall(wall, transaction_manager = null) {
        if (this.walls.indexOf(wall) >= 0) return;
        {
            if (transaction_manager) transaction_manager.push_item_set(this, "walls");
            this.walls.push(wall);
        }

        //*** insert vertices
        for (var s = 0; s < 2; s++) {
            if (this.vertices.indexOf(wall.vertices[s]) < 0) {
                if (transaction_manager) transaction_manager.push_item_set(this, "vertices");
                this.vertices.push(wall.vertices[s]);
            }
        }
        this._need_rebuild_spaces = true;
        return true;
    }

    //***********************************************************************************
    //**** Remove one wall
    //***********************************************************************************
    /**
     *
     * @param {cn_wall} wall
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    remove_wall(wall, transaction_manager = null) {
        var index = this.walls.indexOf(wall);
        if (index < 0) return;

        if (transaction_manager) transaction_manager.push_item_set(this, "walls");
        this.walls.splice(index, 1);

        //*** remove wall from vertices
        for (var side = 0; side < 2; side++) {
            const vi = wall.vertices[side].walls.indexOf(wall);
            if (vi >= 0) wall.vertices[side].walls.splice(vi, 1);
        }

        //*** remove wall from supported vertices */
        wall.supported_vertices.forEach(v => wall.disconnect_vertex(v, transaction_manager));
    }

    /**
     *
     * @param {cn_vertex} vertex
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    remove_vertex(vertex, transaction_manager = null) {
        var index = this.vertices.indexOf(vertex);
        if (index < 0) return;

        if (transaction_manager) transaction_manager.push_item_set(this, "vertices");
        this.vertices.splice(index, 1);
    }

    //***********************************************************************************
    /**
     * Split a wall by inserting a vertex in it.
     * @param {cn_wall} wall : the wall to split
     * @param {cn_vertex} new_vertex : the vertex that splits the wall
     * @param {cn_transaction_manager} transaction_manager
     * @returns {cn_wall} new wall created
     */
    split_wall(wall, new_vertex, transaction_manager = null) {
        const in_scene = (this.walls.indexOf(wall) >= 0);

        if (in_scene && this.vertices.indexOf(new_vertex) < 0) {
            if (transaction_manager) transaction_manager.push_item_set(this, "vertices");
            this.vertices.push(new_vertex);
        }
        var new_wall = wall.split(new_vertex, transaction_manager);
        if (in_scene && this.walls.indexOf(new_wall) < 0) {
            if (transaction_manager) transaction_manager.push_item_set(this, "walls");
            this.walls.push(new_wall);
        }

        return new_wall;
    }

    //***********************************************************************************
    /**
     * Merge two walls into one.
     * Walls must have one vertex in common, otherwise without effect.
     * first wall in argumentwill be kept.
     * @param {cn_wall} wall0
     * @param {cn_wall} wall1
     * @param {cn_vertex} vertex
     * @param {cn_transaction_manager} transaction_manager
     */
    merge_wall(wall0, wall1, vertex, transaction_manager) {
        //*** search for common vertex
        var i0 = wall0.vertices.indexOf(vertex);
        var i1 = wall1.vertices.indexOf(vertex);
        if (i0 < 0 || i1 < 0) return;

        var index = this.walls.indexOf(wall1);
        if (index >= 0) {
            if (transaction_manager) transaction_manager.push_item_set(this, "walls");
            this.walls.splice(index, 1);
        }

        index = this.vertices.indexOf(vertex);
        if (index >= 0) {
            if (transaction_manager) transaction_manager.push_item_set(this, "vertices");
            this.vertices.splice(index, 1);
        }

        //*** save openings */
        var openings = [];
        var opening_positions = [];
        [...wall0.openings, ...wall1.openings].forEach(o => {
            openings.push(o);
            opening_positions.push(o.get_position());
        });

        if (transaction_manager) transaction_manager.push_item_set(wall0, ["vertices", "openings"]);
        wall0.vertices[i0] = wall1.vertices[1 - i1];

        wall0.openings = [];
        const v0 = wall0.vertex_position(0);
        const v1 = wall0.vertex_position(1);
        const dir = cn_sub(v1, v0);
        cn_normalize(dir);
        for (var k = 0; k < openings.length; k++) {
            wall0.openings.push(openings[k]);
            if (transaction_manager) transaction_manager.push_item_set(openings[k], ["wall", "position"]);
            openings[k].wall = wall0;
            openings[k].position = cn_dot(dir, cn_sub(opening_positions[k], v0));
        }
    }

    set_walls_axis(walls, new_axis, transaction_manager) {

        if (transaction_manager) {
            transaction_manager.push_transaction('Modification de l\'axe des murs', '', () => {
                this.full_update();
            });
        }

        walls.forEach(w => {
            if (transaction_manager) {
                transaction_manager.push_item_set(w, 'axis');
                transaction_manager.push_item_set(w.vertices[0], 'position');
                transaction_manager.push_item_set(w.vertices[1], 'position');
            }
            let dy = w.compute_y(new_axis) - w.compute_y(w.axis);
            w.axis = new_axis;
            w.vertices.forEach(vertex => {
                let v2 = cn_sub(vertex.position, cn_mul(w.bounds.normal, dy));
                if (vertex.walls.length > 1) {
                    let other_wall = vertex.walls[1 - vertex.walls.indexOf(w)];
                    let other_dir = cn_sub(other_wall.vertices[1].position, other_wall.vertices[0].position);
                    let previous_length = cn_normalize(other_dir);
                    let new_position = cn_intersect_line(v2, w.bounds.direction, vertex.position, other_wall.bounds.direction);
                    let old_position = cn_clone(vertex.position);
                    let disconnect = false;
                    if (new_position) {
                        vertex.position = new_position;
                        if (cn_dot(other_dir, cn_sub(other_wall.vertices[1].position, other_wall.vertices[0].position)) < previous_length * 0.5) {
                            vertex.position = v2;
                            disconnect = true;
                        }
                    }
                    else {
                        vertex.position = v2;
                        disconnect = true;
                    }
                    if (disconnect) {
                        if (transaction_manager) {
                            transaction_manager.push_item_set(this, "vertices");
                            transaction_manager.push_item_set(other_wall, "vertices");
                        }
                        const new_vertex = new cn_vertex(old_position, false, this);
                        this.vertices.push(new_vertex);
                        const vi = other_wall.vertices.indexOf(vertex);
                        other_wall.vertices[vi] = new_vertex;
                    }
                }
                else if (vertex.support_wall)
                    vertex.position = cn_intersect_line(v2, w.bounds.direction, vertex.position, vertex.support_wall.bounds.direction) || v2;
                else
                    vertex.position = v2;
            });
        });
        this.update();
        this.update_deep();
    }
    //***********************************************************************************
    //**** raytrace
    //***********************************************************************************
    raytrace(origin, direction, max_distance, start_vertex = null, start_wall = null) {
        var res = null;
        for (var i in this.walls) {
            var w = this.walls[i];
            if (w == start_wall) continue;
            if (w.vertices[0] == start_vertex || w.vertices[1] == start_vertex) continue;
            var rr = w.raytrace(origin, direction, max_distance);
            if (rr == false) continue;
            res = rr;
            max_distance = res.distance;
        }
        return res;
    }

    /**
     * Raytrace to 4 bounds of walls
     * @param {number[]} origin
     * @param {number[]} direction
     * @param {number} max_distance
     * @param {cn_vertex} start_vertex
     * @param {cn_wall} start_wall
     * @returns {cn_wall_shape_impact}
     */
    raytrace_walls(origin, direction, max_distance, start_vertex = null, start_wall = null) {
        var res = null;
        for (var i in this.walls) {
            var w = this.walls[i];
            if (w == start_wall) continue;
            if (w.vertices[0] == start_vertex || w.vertices[1] == start_vertex) continue;
            var rr = w.raytrace_shape(origin, direction, max_distance);
            if (!rr) continue;
            res = rr;
            max_distance = res.distance;
        }
        return res;
    }

    /**
     * Same as above, except that wall tops that are not free are avoided.
     * @param {number[]} origin
     * @param {number[]} direction
     * @param {number} max_distance
     * @param {cn_vertex} start_vertex
     * @param {cn_wall} start_wall
     * @returns {cn_wall_shape_impact}
     */
    raytrace_support_shape(origin, direction, max_distance, start_vertex = null, start_wall = null) {
        var res = null;
        for (var i in this.walls) {
            var w = this.walls[i];
            if (w == start_wall) continue;
            if (w.vertices[0] == start_vertex || w.vertices[1] == start_vertex) continue;
            var rr = w.raytrace_support_shape(origin, direction);
            if (!rr || rr.distance >= max_distance) continue;
            res = rr;
            max_distance = res.distance;
        }
        return res;
    }

    /**
     * Raytrace to space inner limits
     * @param {number[]} origin
     * @param {number[]} direction
     * @param {number} max_distance
     * @param {cn_space} avoided_space
     * @returns
     */
    raytrace_spaces(origin, direction, max_distance = 10000, avoided_space = null) {
        var res = null;
        let max_d = max_distance;
        this.spaces.forEach(space => {
            if (space == avoided_space) return;
            const impact = space.raytrace(origin, direction, max_d);
            if (impact) {
                res = impact;
                max_d = impact.distance;
            }
        });
        return res;
    }

    /**
     * Returns the list of spaces along the path.
     * @param {number[]} p0
     * @param {number[]} p1
     * @returns {{space:cn_space, distance:number}[]}
     */
    get_spaces_on_path(p0, p1) {
        const delta = 0.01;
        var direction = cn_sub(p1, p0);
        var max_distance = cn_normalize(direction);
        var origin = cn_add(p0, cn_mul(direction, delta));
        const impact_distances = [0];
        while (true) {
            const impact = this.raytrace_spaces(origin, direction, max_distance - delta);
            if (!impact)
                break;
            impact.distance += delta;
            max_distance -= impact.distance;
            impact_distances.push(impact_distances[impact_distances.length - 1] + impact.distance);
            origin = cn_add(impact.point, cn_mul(direction, delta));
        }
        impact_distances.push(impact_distances[impact_distances.length - 1] + max_distance);

        var spaces = [];
        for (var i = 0; i < impact_distances.length - 1; i++) {
            if (impact_distances[i + 1] - impact_distances[i] > 0.01) {
                const p = cn_add(p0, cn_mul(direction, 0.5 * (impact_distances[i] + impact_distances[i + 1])));
                let space = this.find_space(p, true);
                if (space)
                    spaces.push({ space: space, distance: impact_distances[i + 1] - impact_distances[i] });
            }
        }
        if (spaces.length == 0) {
            let space = this.find_space(p0, true);
            if (space)
                spaces.push({ space: space, distance: 0 });

        }
        return spaces;
    }

    /**
     * returns all impacts between p0 and p1.
     * @param {number[]} p0
     * @param {number[]} p1
     * @return {any}
     */
    pathtrace(p0, p1) {
        var direction = cn_sub(p1, p0);
        const max_distance = cn_normalize(direction);
        var impacts = [];
        for (var i in this.walls) {
            var w = this.walls[i];
            var rr = w.raytrace(p0, direction, max_distance);
            if (rr) impacts.push(rr);
        }
        impacts.sort(function (i0, i1) {
            if (i0.distance < i1.distance) return 1;
            return -1;
        });
        return impacts;
    }

    //***********************************************************************************
    //**** Returns bounding box
    //***********************************************************************************
    get_bounding_box(use_markers = true, use_samplings = false, scale = 0, use_background_maps = true) {
        var box = new cn_box();
        for (var i in this.vertices)
            box.enlarge_point(this.vertices[i].position);

        [
            ...this.slab_openings,
            ...this.stairs,
            ...this.object_instances,
            ...this.beams,
            ...this.columns,
            ...this.pipes,
            ...this.mmm_elements,
        ].forEach(element => {
            box.enlarge_box(element.get_bounding_box());
        });

        if (use_background_maps && this.storey && this.storey.background_maps && this.storey.background_maps.length) {
            this.storey.background_maps.filter(bm => bm.background_opacity > 0).forEach(bm => {
                box.enlarge_box(bm.get_bounding_box());
            });
        }

        if ((use_markers || use_samplings) && this.storey) {
            if (use_markers) {
                this.storey.markers.forEach(element => box.enlarge_box(element.get_bounding_box(scale)));
            }
            if (use_samplings) {
                this.storey.samplings.forEach(element => box.enlarge_box(element.get_bounding_box(scale)));
            }
        }

        return box;
    }

    //***********************************************************************************
    //**** iterate utilities
    //***********************************************************************************
    iterate_vertices(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.vertices)
            callback(this.vertices[i]);
    }

    iterate_walls(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.walls)
            callback(this.walls[i]);
    }

    iterate_openings(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.walls) {
            var wall = this.walls[i];
            for (var j in wall.openings)
                callback(wall.openings[j]);
        }
    }

    iterate_spaces(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.spaces)
            callback(this.spaces[i]);
    }

    iterate_slab_openings(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.slab_openings)
            callback(this.slab_openings[i]);
    }

    iterate_stairs(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.stairs)
            callback(this.stairs[i]);
    }

    iterate_object_instances(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.object_instances)
            callback(this.object_instances[i]);
    }

    iterate_beams(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.beams)
            callback(this.beams[i]);
    }

    iterate_columns(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.columns)
            callback(this.columns[i]);
    }

    iterate_pipes(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.pipes)
            callback(this.pipes[i]);
    }

    iterate_mmm_elements(callback) {
        if (typeof (callback) != 'function') return;
        for (var i in this.mmm_elements)
            callback(this.mmm_elements[i]);
    }

    /**
     * Calls a function on each element of the roof.
     * @param {function} callback
     * @returns
     */
    iterate_elements(callback) {
        if (typeof (callback) != 'function') return;
        this.vertices.forEach(v => callback(v));
        this.walls.forEach(v => callback(v));
        this.spaces.forEach(v => callback(v));
        this.slab_openings.forEach(v => callback(v));
        this.stairs.forEach(v => callback(v));
        this.object_instances.forEach(v => callback(v));
        this.beams.forEach(v => callback(v));
        this.columns.forEach(v => callback(v));
        this.pipes.forEach(v => callback(v));
        this.mmm_elements.forEach(v => callback(v));
    }

    //***********************************************************************************
    //**** Update height
    //***********************************************************************************
    update_height() {
        for (var i in this.stairs)
            this.stairs[i].compute_stairs();
    }

    //***********************************************************************************
    //**** Set selectable. Provide element type
    //***********************************************************************************
    set_selectable(value, element_type = '') {
        if (typeof (element_type) == 'object') {
            // @ts-ignore
            for (var i in element_type)
                // @ts-ignore
                this.set_selectable(value, element_type[i]);
            return;
        }

        if (element_type == 'vertex' || element_type == '') {
            for (var i in this.vertices) this.vertices[i].selectable = value;
        }
        if (element_type == 'wall' || element_type == '') {
            for (var i in this.walls) {
                if (!this.walls[i].balcony)
                    this.walls[i].selectable = value;
            }
        }
        if (element_type == 'balcony' || element_type == '') {
            for (var i in this.walls) {
                if (this.walls[i].balcony)
                    this.walls[i].selectable = value;
            }
        }
        if (element_type == 'opening' || element_type == '') {
            for (var i in this.walls) {
                for (var j in this.walls[i].openings) {
                    this.walls[i].openings[j].selectable = value;
                }
            }
        }
        if (element_type == 'window' || element_type == 'door') {
            for (var i in this.walls) {
                for (var j in this.walls[i].openings) {
                    if (this.walls[i].openings[j].opening_type.category == element_type)
                        this.walls[i].openings[j].selectable = value;
                }
            }
        }
        if (element_type == 'space' || element_type == '') {
            for (var i in this.spaces) {
                this.spaces[i].selectable = value;
            }
        }
        if (element_type == 'slab_opening' || element_type == '') {
            for (var i in this.slab_openings) {
                this.slab_openings[i].selectable = value;
            }
        }
        if (element_type == 'stairs' || element_type == '') {
            for (var i in this.stairs) {
                this.stairs[i].selectable = value;
            }
        }
        if (element_type == 'object' || element_type == '') {
            for (var i in this.object_instances) {
                this.object_instances[i].selectable = value;
            }
        }
        if (element_type == 'beam' || element_type == '') {
            for (var i in this.beams) {
                this.beams[i].selectable = value;
            }
        }
        if (element_type == 'column' || element_type == '') {
            for (var i in this.columns) {
                this.columns[i].selectable = value;
            }
        }

        if (element_type == 'pipe' || element_type == '') {
            for (var i in this.pipes) {
                this.pipes[i].selectable = value;
            }
        }

        if (element_type == 'mmm_element' || element_type == '') {
            for (var i in this.mmm_elements) {
                this.mmm_elements[i].selectable = value;
            }
        }

        if (element_type == 'area_trimming' || element_type == '') {
            for (var i in this.area_trimmings) {
                this.area_trimmings[i].selectable = value;
            }
        }
        if (element_type == 'facing_trimming' || element_type == '') {
            for (var i in this.facing_trimmings) {
                this.facing_trimmings[i].selectable = value;
            }
        }
    }

    //***********************************************************************************
    /**
     * performs a translation
     * @param {number[]} offset : translation offset
     */
    perform_translation(offset) {
        function transform_point(p) {
            p[0] += offset[0];
            p[1] += offset[1];
        }

        for (var i in this.vertices) {
            this.vertices[i].vertex_operation(transform_point);
        }

        this.spaces.forEach(space => space.vertex_operation(transform_point));

        for (var i in this.slab_openings) {
            this.slab_openings[i].vertex_operation(transform_point);
        }

        for (var i in this.stairs) {
            this.stairs[i].vertex_operation(transform_point);
        }

        for (var i in this.object_instances) {
            this.object_instances[i].vertex_operation(transform_point);
        }
        for (var i in this.beams) {
            this.beams[i].vertex_operation(transform_point);
        }
        for (var i in this.columns) {
            this.columns[i].vertex_operation(transform_point);
        }
        for (var i in this.pipes) {
            this.pipes[i].vertex_operation(transform_point);
        }

        for (var i in this.mmm_elements) {
            this.mmm_elements[i].vertex_operation(transform_point);
        }

        for (var i in this.area_trimmings) {
            this.area_trimmings[i].vertex_operation(transform_point);
        }

        for (var i in this.facing_trimmings) {
            this.facing_trimmings[i].vertex_operation(transform_point);
        }

        if (this.storey) {
            this.storey.markers.forEach(marker => marker.vertex_operation(transform_point));
            this.storey.samplings.forEach(sampling => sampling.vertex_operation(transform_point));
        }

        this.full_update();
    }

    //***********************************************************************************
    /**
     * performs a rotation of the full scene
     * @param {number[]} center : world position of rotation center
     * @param {number} angle : rotation angle, in degrees
     */
    perform_rotation(center, angle) {
        var rad_angle = angle * Math.PI / 180;
        var cosangle = Math.cos(rad_angle);
        var sinangle = Math.sin(rad_angle);

        function transform_point(p) {
            var d = cn_sub(p, center);
            p[0] = center[0] + d[0] * cosangle - d[1] * sinangle;
            p[1] = center[1] + d[0] * sinangle + d[1] * cosangle;
        }

        for (var i in this.vertices) {
            this.vertices[i].vertex_operation(transform_point);
        }

        this.spaces.forEach(space => space.vertex_operation(transform_point));

        for (var i in this.slab_openings) {
            this.slab_openings[i].vertex_operation(transform_point);
        }

        for (var i in this.stairs) {
            this.stairs[i].perform_rotation(center, rad_angle, transform_point);
        }

        for (var i in this.object_instances) {
            this.object_instances[i].perform_rotation(center, rad_angle, transform_point);
        }
        for (var i in this.beams) {
            this.beams[i].vertex_operation(transform_point);
        }
        for (var i in this.columns) {
            this.columns[i].perform_rotation(center, rad_angle, transform_point);
        }
        for (var i in this.pipes) {
            this.pipes[i].vertex_operation(transform_point);
        }

        this.mmm_elements.forEach(e => e.vertex_operation(transform_point));

        for (var i in this.area_trimmings) {
            this.area_trimmings[i].vertex_operation(transform_point);
        }

        for (var i in this.facing_trimmings) {
            this.facing_trimmings[i].vertex_operation(transform_point);
        }

        if (this.storey) {
            this.storey.markers.forEach(marker => marker.vertex_operation(transform_point));
            this.storey.samplings.forEach(sampling => sampling.vertex_operation(transform_point));
        }

        this.full_update();
    }

    //***********************************************************************************
    /**
     * performs a flip of the full scene
     * @param {number[]} center : world position of rotation center
     * @param {boolean} horizontal : true for an horizontal flip, false for a vertical
     */
    perform_flip(center, horizontal) {

        function transform_point(p) {
            if (horizontal)
                p[0] = center[0] * 2 - p[0];
            else
                p[1] = center[1] * 2 - p[1];
        }

        for (var i in this.vertices) {
            this.vertices[i].vertex_operation(transform_point);
        }

        for (var i in this.walls) {
            this.walls[i].reverse();
        }

        this.spaces.forEach(space => space.vertex_operation(transform_point));

        for (var i in this.slab_openings) {
            this.slab_openings[i].perform_flip(center, horizontal, transform_point);
        }

        for (var i in this.stairs) {
            this.stairs[i].perform_flip(center, horizontal, transform_point);
        }

        for (var i in this.object_instances) {
            this.object_instances[i].perform_flip(center, horizontal, transform_point);
        }
        for (var i in this.beams) {
            this.beams[i].vertex_operation(transform_point);
        }
        for (var i in this.columns) {
            this.columns[i].perform_flip(center, horizontal, transform_point);
        }
        for (var i in this.pipes) {
            this.pipes[i].vertex_operation(transform_point);
        }

        this.mmm_elements.forEach(e => e.vertex_operation(transform_point));

        for (var i in this.area_trimmings) {
            this.area_trimmings[i].perform_flip(center, horizontal, transform_point);
        }

        for (var i in this.facing_trimmings) {
            this.facing_trimmings[i].perform_flip(center, horizontal, transform_point);
        }

        if (this.storey) {
            this.storey.markers.forEach(marker => marker.vertex_operation(transform_point));
            this.storey.samplings.forEach(sampling => sampling.vertex_operation(transform_point));
        }

        this.full_update();
    }

    //***********************************************************************************
    /**
     * Add an element
     * @param {cn_element} element
     */
    add_element(element) {
        element['scene'] = this;
        element['space'] = null;
        element.regenerate_id();
        element.parent = this;

        if (element.constructor == cn_slab_opening)
            this.slab_openings.push(element);
        if (element.constructor == cn_stairs)
            this.stairs.push(element);
        if (element.constructor == cn_object_instance)
            this.object_instances.push(element);
        if (element.constructor == cn_beam)
            this.beams.push(element);
        if (element.constructor == cn_column)
            this.columns.push(element);
        if (element.constructor == cn_pipe)
            this.pipes.push(element);
        if (element.constructor == cn_mmm_element)
            this.mmm_elements.push(element);
        if (element.constructor == cn_area_trimming)
            this.area_trimmings.push(element);
        if (element.constructor == cn_facing_trimming)
            this.facing_trimmings.push(element);
        if (element.constructor == cn_space)
            this.spaces.push(element);

        if (element['update_space']) element['update_space']();
    }

    //***********************************************************************************
    /**
     * Add a list of elements
     * @param {cn_element[]} elements
     */
    add_elements(elements) {
        for (var i in elements)
            this.add_element(elements[i]);
    }

    //***********************************************************************************
    /**
     * Add layer to draw
     * @param {Array<cn_layer>} layers
     */
    add_layer_to_draw(...layers) {
        this.draw_layers.push(...layers);
    }

    //***********************************************************************************
    /**
     * Remove layer to draw
     * @param {Array<cn_layer>} layers
     */
    remove_layer_to_draw(...layers) {
        layers.forEach(layer => {
            this.draw_layers.splice(this.draw_layers.findIndex(z => z.ID === layer.ID), 1);
        });
    }


    //***********************************************************************************
    /**
     * Add space type to draw
     * @param {Array<cn_space_type>} space_types
     */
    add_space_type_to_draw(...space_types) {
        this.draw_space_types.push(...space_types);
    }

    //***********************************************************************************
    /**
     * Remove space type to draw
     * @param {Array<cn_space_type>} space_types
     */
    remove_space_type_to_draw(...space_types) {
        space_types.forEach(sp => {
            this.draw_space_types.splice(this.draw_space_types.findIndex(z => z.ID === sp.ID), 1);
        });
    }

    //***********************************************************************************
    /**
     * Set zone to draw
     * @param {Array<string>} zones
     */
    set_zone_to_draw(...zones) {
        this.unset_zone_to_draw();
        this.draw_zones.push(...zones);
    }


    /**
     * Highlight heated spaces
     *
     * @param {boolean} is_active
     */
    highlight_heated_spaces(is_active) {
        this._highlight_heated_spaces = is_active;
    }

    //***********************************************************************************
    /**
     * Unset zone to draw
     */
    unset_zone_to_draw() {
        this.draw_zones.length = 0;
    }

    //***********************************************************************************
    /**
     * Add zone to draw
     * @param {Array<string>} zones
     */
    add_zone_to_draw(...zones) {
        this.draw_zones.push(...zones);
    }

    //***********************************************************************************
    /**
     * Remove zone to draw
     * @param {Array<string>} zones
     */
    remove_zone_to_draw(...zones) {
        zones.forEach(zone => {
            this.draw_zones.splice(this.draw_zones.indexOf(zone), 1);
        });
    }


    //*******************************************************
    /**
     * Find an element by constructor name and index
     * @param {string} constructor_name
     * @param {number} index
     * @returns {object}
     */
    find_element(constructor_name, index) {
        var obj = null;
        if (constructor_name == 'cn_vertex')
            obj = this.vertices[index];
        else if (constructor_name == 'cn_wall')
            obj = this.walls[index];
        else if (constructor_name == 'cn_space')
            obj = this.spaces[index];
        else if (constructor_name == 'cn_slab_opening')
            obj = this.slab_openings[index];
        else if (constructor_name == 'cn_stairs')
            obj = this.stairs[index];
        else if (constructor_name == 'cn_object_instance')
            obj = this.object_instances[index];
        else if (constructor_name == 'cn_beam')
            obj = this.beams[index];
        else if (constructor_name == 'cn_column')
            obj = this.columns[index];
        else if (constructor_name == 'cn_pipe')
            obj = this.pipes[index];
        else if (constructor_name == 'cn_mmm_element')
            obj = this.mmm_elements[index];
        else if (constructor_name == 'cn_area_trimming')
            obj = this.area_trimmings[index];
        else if (constructor_name == 'cn_facing_trimming')
            obj = this.facing_trimmings[index];

        if (obj) return obj;
        return null;
    }

    //*******************************************************
    /**
     * returns a kist of all elements in the scene
     * @returns {Array<cn_element>}
     */
    get_elements() {
        return [
            ...this.vertices,
            ...this.walls,
            ...this.get_openings(false),
            ...this.spaces,
            ...this.object_instances,
            ...this.slab_openings,
            ...this.stairs,
            ...this.beams,
            ...this.columns,
            ...this.pipes,
            ...this.mmm_elements,
            ...this.area_trimmings,
            ...this.facing_trimmings
        ];
    }

    //*******************************************************
    /**
     * Find an element by its id
     * @param {string} id
     * @returns {object}
     */
    find_element_by_id(id) {
        const obj = this.get_elements().find(e => e.ID == id);
        if (obj) return obj;
        return null;
    }

    _merge_splited_spaces_zones(old_space, new_space) {
        Object.entries(this.building.zones).forEach(([zoning_type, zones]) => {
            zones.forEach(zone => {
                const new_rooms = [];
                zone.rooms
                    .filter(room => room.space === old_space.ID && room.storey === this.storey.ID)
                    .forEach(room => new_rooms.push({ space: new_space.ID, storey: this.storey.ID }))
                zone.rooms.push(...new_rooms);
            });
        });
    }

    /**
     * Returns true if the topology date (i.e. creation or removal of walls or vertices) is lower or equal than input date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_topology(date) {
        if (this.get_date() <= date) return true;
        return (this.get_date('walls') <= date && this.get_date('vertices') <= date);
    }

    /**
     * returns true if wall geometry is lower or equal than input date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_walls(date) {
        if (this.get_date() <= date) return true;
        if (this.get_date('walls') > date) return false;
        if (this.get_date('vertices') > date) return false;
        if (this.walls.some(w => !w.up_to_date_geometry(date))) return false;
        return true;
    }

    set_parent(parent) {

    }

    compute_facing_count() {
        [...this.walls,
        ...this.object_instances,
        ...this.pipes,
        ...this.beams,
        ...this.columns,
        ...this.stairs,
        ...this.spaces,
        ].forEach(e => e.compute_facing_count());
    }
}

